import React, { useState, useEffect } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Label,
  Input,
  FormFeedback,
  Form,
} from 'reactstrap';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { toast } from 'react-toastify';
import { updateUserProfile } from 'services/authService';
import { useDispatch } from 'react-redux';
import { isVisible } from 'slices/loader/reducer';
import { User } from 'utility/interface';

const UserProfile = () => {
  const [name, setName] = useState('User');
  const [nameInitials, setNameInitials] = useState('U');
  const [email, setEmail] = useState('admin@gmail.com');
  const [phone, setPhone] = useState('');
  const [idx, setIdx] = useState('1');
  const [role, setRole] = useState('Admin');
  const dispatch = useDispatch();

  useEffect(() => {
    const authUser = sessionStorage.getItem('authUser');
    if (authUser) {
      const obj = JSON.parse(authUser);
      setName(obj?.name || 'User');
      setNameInitials(
        obj?.name
          ?.split(' ')
          .filter(Boolean)
          .map(part => part[0].toUpperCase())
          .join(''),
      );
      setEmail(obj?.email || 'admin@gmail.com');
      setPhone(obj?.phone || '');
      setIdx(obj?.id || '1');
      setRole(obj?.accessLevels?.[0] || 'Admin');
    }
  }, []);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: name || '',
      nameInitials: nameInitials || '',
      email: email || '',
      phone: phone || '',
      password: '',
      confirmPassword: '',
      idx: idx || '',
    },
    validationSchema: Yup.object({
      name: Yup.string().required('Please enter your name'),
      email: Yup.string()
        .email('Invalid email')
        .required('Please enter your email'),
      phone: Yup.string().required('Please enter your phone number'),
      password: Yup.string().min(6, 'Password must be at least 6 characters'),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref('password'), null], 'Passwords must match')
        .when('password', {
          is: (val: string) => val && val.length > 0,
          then: schema => schema.required('Please confirm your password'),
          otherwise: schema => schema,
        }),
    }),
    onSubmit: async values => {
      dispatch(isVisible(true));
      try {
        const payload: Partial<User> = {
          name: values.name,
          email: values.email,
          phone: values.phone,
        };

        // ✅ Only include password if both fields are non-empty and match
        if (
          values.password.trim() !== '' &&
          values.confirmPassword.trim() !== '' &&
          values.password === values.confirmPassword
        ) {
          payload.password = values.password;
        }

        const result = await updateUserProfile(Number(values.idx), payload);

        if (result) {
          const currentUser = JSON.parse(
            sessionStorage.getItem('authUser') || '{}',
          );
          const isChanged =
            currentUser?.name !== result.name ||
            currentUser?.email !== result.email ||
            currentUser?.phone !== result.phone;

          if (isChanged) {
            toast.success('Profile updated successfully');
            sessionStorage.setItem('authUser', JSON.stringify(result));
            setName(result.name);
            setNameInitials(
              result.name
                ?.split(' ')
                .filter(Boolean)
                .map(part => part[0].toUpperCase())
                .join(''),
            );
            setEmail(result.email);
            setPhone(result.phone);
            setIdx(result.id);
            setRole(result.accessLevels?.[0]);
            window.dispatchEvent(new Event('profileUpdated'));
          }
        }
      } catch (err) {
        toast.error(err.message || 'Failed to update profile'); // ✅ Optional
      } finally {
        dispatch(isVisible(false));
      }
    },
  });

  document.title = 'Profile | MPM - Mazar Properties Management';

  return (
    <React.Fragment>
      <div className="page-content mt-lg-5">
        <Container fluid>
          <Row>
            <Col lg="12">
              <Card>
                <CardBody>
                  <div className="d-flex">
                    <div className="mx-3">
                      <div
                        className="rounded-circle d-flex align-items-center justify-content-center"
                        style={{
                          width: '50px',
                          height: '50px',
                          backgroundColor: '#343a40', // dark grey
                          color: 'white',
                          fontWeight: 'bold',
                          textTransform: 'uppercase',
                          fontSize: '14px',
                        }}
                      >
                        {nameInitials}
                      </div>
                    </div>
                    <div className="flex-grow-1 align-self-center">
                      <div className="text-muted">
                        <h5>{name}</h5>
                        <h6>{role}</h6>
                        <p className="mb-1">Email Id: {email}</p>
                        <p className="mb-0">UserId: {idx}</p>
                      </div>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          <h4 className="card-title mb-4">Update Profile</h4>

          <Card>
            <CardBody>
              <Form
                className="form-horizontal"
                onSubmit={e => {
                  e.preventDefault();
                  validation.handleSubmit();
                  return false;
                }}
              >
                <div className="form-group mt-3">
                  <Label className="form-label">Name</Label>
                  <Input
                    name="name"
                    type="text"
                    className="form-control"
                    placeholder="Enter Name"
                    value={validation.values.name}
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    invalid={
                      validation.touched.name && !!validation.errors.name
                    }
                  />
                  <FormFeedback>{validation.errors.name}</FormFeedback>
                </div>
                <div className="form-group mt-3">
                  <Label className="form-label">Email</Label>
                  <Input
                    name="email"
                    type="email"
                    className="form-control"
                    placeholder="Enter Email"
                    value={validation.values.email}
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    invalid={
                      validation.touched.email && !!validation.errors.email
                    }
                  />
                  <FormFeedback>{validation.errors.email}</FormFeedback>
                </div>

                <div className="form-group mt-3">
                  <Label className="form-label">Phone</Label>
                  <Input
                    name="phone"
                    type="text"
                    className="form-control"
                    placeholder="Enter Phone Number"
                    value={validation.values.phone}
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    invalid={
                      validation.touched.phone && !!validation.errors.phone
                    }
                  />
                  <FormFeedback>{validation.errors.phone}</FormFeedback>
                </div>

                <div className="form-group mt-3">
                  <Label className="form-label">Password</Label>
                  <Input
                    name="password"
                    type="password"
                    className="form-control"
                    placeholder="Enter New Password"
                    value={validation.values.password}
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    invalid={
                      validation.touched.password &&
                      !!validation.errors.password
                    }
                  />
                  <FormFeedback>{validation.errors.password}</FormFeedback>
                </div>

                <div className="form-group mt-3">
                  <Label className="form-label">Confirm Password</Label>
                  <Input
                    name="confirmPassword"
                    type="text"
                    className="form-control"
                    placeholder="Confirm New Password"
                    value={validation.values.confirmPassword}
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    invalid={
                      validation.touched.confirmPassword &&
                      !!validation.errors.confirmPassword
                    }
                  />
                  <FormFeedback>
                    {validation.errors.confirmPassword}
                  </FormFeedback>
                </div>

                <Input name="idx" value={idx} type="hidden" />

                <div className="text-center mt-4">
                  <Button type="submit" color="danger">
                    Update Profile
                  </Button>
                </div>
              </Form>
            </CardBody>
          </Card>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default UserProfile;
